import React, { useState } from "react";
import moment from "moment";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import ListItemText from "@mui/material/ListItemText";

import { ASSET_REPLAY_LOGS_QUERY } from "../../constants/graphql/queries";

import ReservedInventoryList from "./ReservedInventoryList";
import AssetReplayLogRackLocation from "src/views/asset/AssetReplayLogRackLocation";
import useAuth from "src/hooks/useAuth";

const DATE_FORMAT = "MM/DD/yyyy hh:mm A";
const serverFormat = "yyyy-MM-DDThh:mm:ss Z";

const TAG_MAP = {
  /* Inbound Acttions (Available Quantity increases) */
  inbound: { label: "Inbound", color: "#D5F5D5" },
  delete_inventory_item_order: { label: "Return from Order", color: "#D5F5D5" },

  /* Outbound Actions (Available Quantity decreases) */
  outbound: { label: "Outbound", color: "#F8D7DA" },
  add_inventory_item_order: { label: "Added to Order", color: "#F8D7DA" },

  /* Reserved Action (Reserved Quantity changes) */
  reserved: { label: "Reserved", color: "#FFE5CC" },
  add_inventory_item_reservation: { label: "Added to Reservation", color: "#FFE5CC" },
  delete_inventory_item_reservation: { label: "Return from Reservation", color: "#FFE5CC" },

  /* Rack_location Changes */
  picked: { label: "Picked", color: "#CCE7FF" },
  rack_location_changed: { label: "Location Change", color: "#CCE7FF" },
  location_adjustment: { label: "Location Adjustment", color: "#CCE7FF" },

  /* General Actions */
  general: { label: "Creation", color: "#E0E0E0" },

  /* Internal Work Order Actions */
  internal_item_added: { label: "Added to Internal Order", color: "#F8D7DA" },
  internal_item_removed: { label: "Returned from Internal Order", color: "#D5F5D5" },
  internal_item_updated: { label: "Updated in Internal Order", color: "#FFCC00" },
  moved_for_internal_work_order: { label: "Moved for Internal Order", color: "#E8D4F8" },

  /* Inventory Request Staging Actions */
  moved_to_staging_area_for_inventory_request: { label: "Moved to Staging Area", color: "#D4E8F8" },
};

function getTagDetails(tag) {
  return TAG_MAP[tag] || { label: "Unknown", color: "#fff" };
}

export default function AssetReplayLogs({ assetId, defaultSelected = "", displayFilter = true }) {
  const [selected, setSelected] = useState(defaultSelected);
  const { user } = useAuth();
  const isReservedInventory = selected === "reserved_inventory";
  const { settings } = user.company;
  const { data, loading } = useQuery(ASSET_REPLAY_LOGS_QUERY, {
    variables: { assetId },
    fetchPolicy: "network-only",
  });

  const assetReplayLogs = data?.assetReplayLogs?.collection || [];

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography fontWeight={500} fontSize={18}>
            Inventory Replay Log
          </Typography>
          {!isReservedInventory && (
            <Box mt={1} fontSize={14}>
              {assetReplayLogs.length} Records
            </Box>
          )}
        </Box>
        {displayFilter && (
          <>
            <Box>
              {isReservedInventory && (
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ textTransform: "none" }}
                  onClick={() => setSelected("")}
                >
                  Reserved Inventory
                  <CloseIcon sx={{ ml: 0.5 }} />
                </Button>
              )}
            </Box>
            <Box>
              <PopupState variant="popover" popupId="demo-popup-menu">
                {popupState => (
                  <React.Fragment>
                    {settings.reservations && (
                      <Button
                        {...bindTrigger(popupState)}
                        variant="outlined"
                        color="primary"
                        sx={{ textTransform: "none" }}
                      >
                        <FilterAltIcon sx={{ mr: 0.5 }} />
                        Filter
                        <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
                      </Button>
                    )}
                    <Menu
                      {...bindMenu(popupState)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                      <MenuItem
                        data-id="select-item"
                        onClick={() => {
                          setSelected("reserved_inventory");
                          popupState.close();
                        }}
                      >
                        <ListItemText primary="Reserved inventory" />
                      </MenuItem>
                    </Menu>
                  </React.Fragment>
                )}
              </PopupState>
            </Box>
          </>
        )}
      </Box>
      {isReservedInventory ? (
        <Box display="flex" mb={2}>
          <ReservedInventoryList assetId={assetId} />
        </Box>
      ) : (
        <Box mb={2} sx={{ overflowY: "auto", width: "100%" }}>
          {loading ? (
            <LinearProgress className="mt-2" />
          ) : assetReplayLogs.length === 0 ? (
            <Box display="flex" justifyContent="center">
              Empty
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Action</TableCell>
                      <TableCell align="center">Performed By</TableCell>
                      <TableCell align="center">Transaction Quantity</TableCell>
                      {settings.inventory_tracking && (
                        <TableCell align="center">Order Number</TableCell>
                      )}
                      {settings.inventory_tracking && (
                        <TableCell align="center">Available Quantity</TableCell>
                      )}
                      {settings.reservations && (
                        <TableCell align="center">Reserved Quantity</TableCell>
                      )}
                      {settings.inventory_tracking && (
                        <TableCell align="left" style={{ minWidth: 160 }}>
                          Storage Location(s)
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assetReplayLogs.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          ...(row.disabledForBilling && {
                            backgroundColor: "#f9fafb",
                            opacity: 0.7,
                            "& td": {
                              textDecoration: "line-through",
                            },
                          }),
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {(() => {
                            const performedDate = moment(row.performedAt, serverFormat).format(
                              DATE_FORMAT
                            );
                            const performedDateOnly = moment(row.performedAt, serverFormat).format(
                              "MM/DD/yyyy"
                            );
                            const actualDateOnly = row.actualReceivedDate
                              ? moment(row.actualReceivedDate, serverFormat).format("MM/DD/yyyy")
                              : null;

                            const isBackdated =
                              actualDateOnly && actualDateOnly !== performedDateOnly;

                            return (
                              <Box>
                                <Box component="div">
                                  {isBackdated ? actualDateOnly : performedDate}
                                </Box>
                                {isBackdated && (
                                  <Box
                                    component="div"
                                    sx={{
                                      textDecoration: "line-through",
                                      color: "#ef4444",
                                      fontSize: "0.875rem",
                                      mt: 0.25,
                                    }}
                                  >
                                    {performedDate}
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                          {row.disabledForBilling && (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                ml: 1,
                                px: 0.75,
                                py: 0.25,
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#ef4444",
                                backgroundColor: "#fee",
                                borderRadius: "4px",
                                textDecoration: "none",
                              }}
                            >
                              DISABLED
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {(() => {
                            const { label, color } = getTagDetails(row.action);
                            return (
                              <span
                                style={{
                                  display: "inline-block",
                                  backgroundColor: color,
                                  color: "#000",
                                  padding: "4px 12px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  textAlign: "center",
                                  lineHeight: "1.4",
                                  minWidth: "100px",
                                  maxWidth: "120px",
                                }}
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="center">{row.user?.email}</TableCell>
                        <TableCell align="center">{row.quantity}</TableCell>
                        {settings.inventory_tracking && (
                          <TableCell align="center">{row.woNumber}</TableCell>
                        )}
                        {settings.inventory_tracking && (
                          <TableCell align="center">{row.availableQty}</TableCell>
                        )}
                        {settings.reservations && (
                          <TableCell align="center">{row.reservedQty}</TableCell>
                        )}
                        {settings.inventory_tracking && (
                          <TableCell align="left">
                            <AssetReplayLogRackLocation rackLocations={row.rackLocations} />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
